import React from 'react';
import { FaSignOutAlt } from 'react-icons/fa';
import { HiComputerDesktop, HiBookOpen } from 'react-icons/hi2';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HomeIcon, SettingIcon, PeopleIcon, MoneyIcon, DocIcon, Doc2Icon, LeaveIcon, StateIcon } from '../Tools/IconSvg';
import { canAccessSettings, canEditHrData, canViewEmployees, canViewFinancialStatements } from '../Auth/permissions';

const MenuNav = React.memo(({ isCollapsed, onLogoutRequest, user }) => {
    const { t } = useTranslation();
    const handleLogoutRequest = () => {
        onLogoutRequest();
    };

    const activeClass =
        'bg-[--bg-color] text-[--color-primary] border-[--color-primary] [&_svg]:text-[--color-primary]';

    const inactiveClass = 'text-[--bg-color]';
    const hoverClass = 'hover:bg-[--bg-hover]  hover:border-[--color-primary]';
    const settingsAllowed = canAccessSettings(user);

    const activeCompany = user?.company_active;
    const hasDailySalaryInput = activeCompany?.has_daily_salary_input || false;

    // Comptable : Tableau de bord et Paie (Journal & Répartition).
    // Client Cabinet : Tableau de bord, Employés et Paie, en consultation.
    const canEdit = canEditHrData(user);
    const menuItems = [
        { path: '/', label: t('sidebar.dashboard'), icon: <HomeIcon className="w-6 h-6" /> },
        canViewEmployees(user) && { path: '/employees', label: t('sidebar.employees'), icon: <PeopleIcon className="w-6 h-6" /> },
        canEdit && { path: '/primes', label: t('sidebar.primes'), icon: <MoneyIcon className="w-6 h-6" /> },
        { path: '/payslip/report', label: t('sidebar.payslip'), icon: <Doc2Icon className="w-6 h-6" /> },
        canEdit && { path: '/leave', label: t('sidebar.leave'), icon: <LeaveIcon className="w-6 h-6" /> },
        canViewFinancialStatements(user) && { path: '/etats', label: t('sidebar.states'), icon: <StateIcon className="w-6 h-6" /> },
    ].filter(Boolean);

    return (
        <div className="flex flex-col font-semibold justify-between h-full text-white py-4">
            <ul className="space-y-4">
                {menuItems.map((item, index) =>
                    item.subItems ? (
                        <li key={index}>
                            <div className="group">
                                <div className={`flex items-center gap-2 py-2 ${isCollapsed ? 'pl-3 justify-center' : 'pl-6'} cursor-pointer`}>
                                    {item.icon} {!isCollapsed && item.label}
                                </div>
                                {!isCollapsed && (
                                    <ul className="ml-4 space-y-2">
                                        {item.subItems.map((subItem, subIndex) => (
                                            <li key={subIndex}>
                                                <NavLink
                                                    to={subItem.path}
                                                    className={({ isActive }) =>
                                                        `flex items-center gap-2 py-2 ${hoverClass} ${isActive ? activeClass : inactiveClass}`
                                                    }
                                                    aria-current={({ isActive }) => (isActive ? 'page' : undefined)}
                                                >
                                                    {({ isActive }) => (
                                                        <>
                                                            {isActive && (
                                                                <div className='border-left border-[3px] h-[30px] justify-start rounded-lg border-orange-400'></div>
                                                            )}
                                                            <div className='flex items-center gap-2 pl-6'>
                                                                {subItem.icon} {subItem.label}
                                                            </div>
                                                        </>
                                                    )}
                                                </NavLink>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </li>
                    ) : (
                        <li key={index}>
                            <NavLink
                                to={item.path}
                                className={({ isActive }) => 
                                    `flex w-full py-2 ${!isActive && hoverClass} ${isActive ? activeClass : inactiveClass} ${isCollapsed ? 'justify-center relative' : ''}`
                                }
                                aria-current={({ isActive }) => (isActive ? 'page' : undefined)}
                                title={isCollapsed ? item.label : ""}
                            >
                                {({ isActive }) => (
                                    <>
                                        {isActive && !isCollapsed && (
                                            <div className='border-[3px] h-[30px] justify-start rounded-lg border-orange-400'></div>
                                        )}
                                        {isActive && isCollapsed && (
                                            <div className='absolute left-0 top-1/2 -translate-y-1/2 border-[3px] h-[30px] rounded-lg border-orange-400'></div>
                                        )}
                                        <div className={`flex items-center gap-2 ${isCollapsed ? 'pl-0 justify-center' : 'pl-4'}`}>
                                            {item.icon} {!isCollapsed && item.label}
                                        </div>
                                    </>
                                )}
                            </NavLink>
                        </li>
                    )
                )}
            </ul>
            <div className={`mt-6 border-t pt-4 ${isCollapsed ? 'text-center' : ''}`}>
                <ul className="space-y-2">
                    <li>
                        <a
                            href="https://yros.io/simulateur/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center gap-2 py-2 ${isCollapsed ? 'justify-center' : 'pl-5'} rounded-md text-white`}
                            title={isCollapsed ? t('sidebar.onlineSimulator') : t('sidebar.onlineSimulatorNewTab')}
                            aria-label={t('sidebar.onlineSimulatorAriaLabel')}
                        >
                            <HiComputerDesktop  className="w-6 h-6" />
                            {!isCollapsed && (
                                <span className="flex items-center gap-1">
                                    {t('sidebar.onlineSimulator')}
                                </span>
                            )}
                        </a>
                    </li>
                    <li>
                        <NavLink
                            to="/guide"
                            className={({ isActive }) => 
                                `flex items-center gap-2 py-2 ${isCollapsed ? 'justify-center relative' : 'pl-5'} rounded-md text-white`
                            }
                            aria-current={({ isActive }) => (isActive ? 'page' : undefined)}
                            title={isCollapsed ? t('sidebar.userGuide') : ""}
                        >
                            {({ isActive }) => (
                                <>
                                    {isActive && isCollapsed && (
                                        <div className='absolute left-0 top-1/2 -translate-y-1/2 border-[3px] h-[30px] rounded-lg border-orange-400'></div>
                                    )}
                                    <div className="flex items-center gap-2">
                                        <HiBookOpen className="w-6 h-6" />
                                        {!isCollapsed && t('sidebar.userGuide')}
                                    </div>
                                </>
                            )}
                        </NavLink>
                    </li>
                    {settingsAllowed && (
                        <li>
                            <NavLink
                                to="/settings"
                                className={({ isActive }) => 
                                    `flex items-center gap-2 py-2 ${isCollapsed ? 'justify-center relative' : 'pl-5'} rounded-md text-white`
                                }
                                aria-current={({ isActive }) => (isActive ? 'page' : undefined)}
                                title={isCollapsed ? t('sidebar.settings') : ""}
                            >
                                {({ isActive }) => (
                                    <>
                                        {isActive && isCollapsed && (
                                            <div className='absolute left-0 top-1/2 -translate-y-1/2 border-[3px] h-[30px] rounded-lg border-orange-400'></div>
                                        )}
                                        <div className="flex items-center gap-2">
                                            <SettingIcon className="w-6 h-6" />
                                            {!isCollapsed && t('sidebar.settings')}
                                        </div>
                                    </>
                                )}
                            </NavLink>
                        </li>
                    )}
                    <li>
                        <button
                            onClick={handleLogoutRequest}
                            className={`flex items-center gap-2 py-2 ${isCollapsed ? 'justify-center w-full' : 'pl-6'} rounded-md text-white`}
                            title={isCollapsed ? t('sidebar.logout') : ""}
                        >
                            <FaSignOutAlt className="text-white" /> {!isCollapsed && t('sidebar.logout')}
                        </button>
                    </li>
                </ul>
            </div>
        </div>
    );
});

export default MenuNav;
